/**
 * Game screen renderer. Reads a `GameState` plus a small view model, owns zero
 * game logic. The only state it keeps is presentational: decay timers for the
 * level-up flash, the hard-drop trail and the award plaque, all driven by the
 * events the host wires up.
 */

import {
  Phase,
  TUNING,
  type ClearAnimation,
  type GameState,
  type LineClearEvent,
} from '../core/game.ts';
import { PIECE_COLORS, type PieceType, type Vec } from '../core/piece.ts';
import type { Cell } from '../core/board.ts';
import {
  Painter,
  drawMusicChip,
  drawSoundChip,
  drawThemeChip,
  drawVolumeChip,
  type VolumeLevel,
} from './painter.ts';
import { HitList } from './hits.ts';
import type { Surface } from './surface.ts';
import { drawMiniPiece } from './pieces.ts';
import { ClearImpact } from './clearImpact.ts';
import {
  drawGameOverOverlay,
  drawPauseOverlay,
  drawReadyOverlay,
  scrim,
  type OverlayBox,
} from './overlays.ts';
import {
  CANVAS_WIDTH,
  FIELD_H,
  FIELD_W,
  FIELD_X,
  FIELD_Y,
  HUD_SLOTS,
  LAYOUT,
  MODE_COLORS,
  RIGHT_X,
  STATS_ROWS,
  TYPE,
  faces,
  formatCounter,
  formatScore,
  onBlock,
  palette,
} from './theme.ts';

/** Everything on the game screen that is not in `GameState`. */
export interface GameView {
  playerName: string;
  /** Best score on the board, for the BEST readout. */
  best: number;
  /** Placement of the run just finished, `null` when it missed the board. */
  rank: number | null;
  mode: 'SOLO' | 'BALANCE' | 'TWIN';
}

/** The transient plaque that reports what a placement just paid. */
interface Award {
  title: string;
  value: string;
  sub: string;
  color: string;
  ms: number;
}

/**
 * The swept cells behind a falling piece. A hard drop paints its whole sweep
 * once; a soft drop keeps re-arming a short streak while the player holds the
 * key, so the piece drags a tail instead of flashing one.
 */
interface Trail {
  cells: readonly Vec[];
  distance: number;
  color: string;
  ms: number;
  lifeMs: number;
  /** Scales the whole trail: a soft drop is gentler than a slam. */
  intensity: number;
}

const TRAIL_MS = 120;
const SOFT_TRAIL_MS = 190;
/** A soft drop trails a few rows, not the whole column it came down. */
const SOFT_TRAIL_ROWS = 4;
const SOFT_TRAIL_INTENSITY = 0.55;
const AWARD_MS = 900;
const AWARD_FADE_MS = 220;

const GAME_KEYS: readonly (readonly [string, string])[] = [
  ['MOVE', '← →'],
  ['SOFT', '↓'],
  ['DROP', 'SPACE'],
  ['ROT', 'Z X ↑'],
  ['180', 'A'],
  ['HOLD', 'C'],
  ['PAUSE', 'ESC'],
  ['RESET', 'R'],
  ['SOUND', 'F3'],
  ['MUSIC', 'F4'],
];

/** Key hints occupy the former NEXT region at the top of the right rail. */
const KEYS_LABEL_Y = 74;
const KEYS_FIRST_Y = 100;
const KEYS_PITCH = 18;

/** NEXT occupies the former key-hint region beside the bottom of the field. */
const NEXT_LABEL_Y = 456;
const NEXT_BOX_Y = 464;
const NEXT_BOX_H = 208;
const NEXT_ROW_HEIGHT = 39;

/** The field, for the overlays that dim it. */
const FIELD_BOX: OverlayBox = { x: FIELD_X, y: FIELD_Y, w: FIELD_W, h: FIELD_H };

export class CanvasRenderer {
  /** Controls painted by the last frame, for pointer hit testing. */
  readonly hits = new HitList();
  private readonly painter: Painter;
  private levelFlashMs = 0;
  private flashedLevel = 0;
  private trail: Trail | null = null;
  private award: Award | null = null;
  private readonly clearImpact = new ClearImpact();
  /** Previous frame's active piece, for spotting soft-drop descent. */
  private lastActive: { type: PieceType; y: number } | null = null;
  /** Rows descended in the current soft-drop run, capped. */
  private softSpan = 0;

  constructor(private readonly surface: Surface) {
    this.painter = surface.painter;
  }

  /** Trigger the accent flash; host wires this to the `onLevelUp` event. */
  flashLevelUp(level = 0): void {
    this.levelFlashMs = TUNING.levelUpFlashMs;
    this.flashedLevel = level;
  }

  /** Paint the swept columns of a hard drop; host wires this to `onLock`. */
  showDropTrail(cells: readonly Vec[], distance: number, type: PieceType): void {
    if (distance <= 0) return;
    this.trail = {
      cells,
      distance,
      color: PIECE_COLORS[type],
      ms: TRAIL_MS,
      lifeMs: TRAIL_MS,
      intensity: 1,
    };
    this.softSpan = 0;
  }

  /** Show what a placement paid. Host wires this to `onLock` / `onLineClear`. */
  showAward(title: string, points: number, sub: string, color: string): void {
    this.award = { title, value: `+${formatScore(points)}`, sub, color, ms: AWARD_MS };
  }

  /** Break cleared cells into debris and kick the field on a chained clear. */
  showMultiLineClear(state: GameState, event: LineClearEvent): void {
    this.clearImpact.trigger(state, event);
  }

  draw(
    state: GameState,
    view: GameView,
    dtMs: number,
    muted = false,
    volume: VolumeLevel = 'medium',
    music = true,
  ): void {
    this.levelFlashMs = Math.max(0, this.levelFlashMs - dtMs);
    if (this.trail) {
      this.trail.ms -= dtMs;
      if (this.trail.ms <= 0) this.trail = null;
    }
    if (this.award) {
      this.award.ms -= dtMs;
      if (this.award.ms <= 0) this.award = null;
    }
    this.clearImpact.update(dtMs);

    this.trackSoftDrop(state);

    this.surface.begin();
    this.hits.clear();

    const dimSides = state.phase === Phase.Paused || state.phase === Phase.GameOver;

    this.drawHeader(state, muted, volume, music);
    this.drawField(state);
    this.withAlpha(dimSides ? 0.4 : 1, () => {
      this.drawLeftColumn(state, view);
      this.drawRightColumn(state, view);
    });
    this.drawOverlay(state, view);
  }

  // ------------------------------------------------------------------ regions

  private drawHeader(
    state: GameState,
    muted: boolean,
    volume: VolumeLevel,
    music: boolean,
  ): void {
    const p = this.painter;
    const c = palette();
    const y = LAYOUT.pad + 8;
    p.typed('KD-TETRIS', LAYOUT.pad, y, TYPE.brand, c.textLabel);

    const status = statusOf(state);
    let right = CANVAS_WIDTH - LAYOUT.pad;
    right -= p.headerChip(status.text, right, y, status.color).w + 8;
    right -= drawThemeChip(p, this.hits, right, y) + 8;
    right -= drawSoundChip(p, this.hits, right, y, muted) + 8;
    right -= drawMusicChip(p, this.hits, right, y, music) + 8;
    right -= drawVolumeChip(p, this.hits, right, y, volume) + 8;

    // Pause has no other pointer affordance; ESC alone is keyboard-only.
    if (state.phase === Phase.Playing) {
      const box = p.headerChip('PAUSE', right, y, { f: c.panelAlt, textColor: c.textDim });
      this.hits.add('game.pause', box.x, box.y, box.w, box.h);
    }

    p.rule(LAYOUT.pad, y + 10, CANVAS_WIDTH - LAYOUT.pad * 2);
  }

  private drawField(state: GameState): void {
    const ctx = this.surface.ctx;
    const p = this.painter;
    const c = palette();
    const f = LAYOUT.frame;

    const shake = this.clearImpact.shakeOffset();
    ctx.save();
    ctx.translate(shake.x, shake.y);

    // The well: an inset frame around a recessed field.
    p.bevelRect(FIELD_X - f, FIELD_Y - f, FIELD_W + f * 2, FIELD_H + f * 2, c.well, f);
    p.fillRect(FIELD_X, FIELD_Y, FIELD_W, FIELD_H, c.field);

    ctx.save();
    ctx.beginPath();
    ctx.rect(FIELD_X, FIELD_Y, FIELD_W, FIELD_H);
    ctx.clip();

    for (let r = 0; r < LAYOUT.rows; r++) {
      for (let col = 0; col < LAYOUT.cols; col++) {
        p.blockCell(
          FIELD_X + col * LAYOUT.cell,
          FIELD_Y + r * LAYOUT.cell,
          LAYOUT.cell,
          c.cellEmpty,
        );
      }
    }

    this.drawTrail();
    this.drawStack(state);
    if (!state.clearAnim) {
      this.drawGhost(state);
      this.drawActive(state);
    }
    this.drawClearBand(state);

    ctx.restore();

    if (this.levelFlashMs > 0) {
      const alpha = this.levelFlashMs / TUNING.levelUpFlashMs;
      ctx.save();
      ctx.globalAlpha = alpha;
      p.bevelRect(FIELD_X - f, FIELD_Y - f, FIELD_W + f * 2, FIELD_H + f * 2, c.accentFaces, f);
      ctx.restore();
      // The frame is drawn over the field, so put the well interior back.
      ctx.save();
      ctx.beginPath();
      ctx.rect(FIELD_X, FIELD_Y, FIELD_W, FIELD_H);
      ctx.clip();
      ctx.globalAlpha = 1;
      this.repaintFieldInterior(state);
      ctx.globalAlpha = alpha;
      p.fillRect(FIELD_X, FIELD_Y, FIELD_W, FIELD_H, c.levelFlash);
      ctx.restore();
      this.drawLevelPlaque(alpha);
    }
    this.clearImpact.draw(p, FIELD_X, FIELD_Y, LAYOUT.cell);
    ctx.restore();
  }

  /**
   * The accent frame of the level-up flash overlaps the field, so the cells
   * under it are laid down again. Cheaper than clipping the frame itself.
   */
  private repaintFieldInterior(state: GameState): void {
    const p = this.painter;
    const c = palette();
    p.fillRect(FIELD_X, FIELD_Y, FIELD_W, FIELD_H, c.field);
    for (let r = 0; r < LAYOUT.rows; r++) {
      for (let col = 0; col < LAYOUT.cols; col++) {
        p.blockCell(FIELD_X + col * LAYOUT.cell, FIELD_Y + r * LAYOUT.cell, LAYOUT.cell, c.cellEmpty);
      }
    }
    this.drawTrail();
    this.drawStack(state);
    if (!state.clearAnim) {
      this.drawGhost(state);
      this.drawActive(state);
    }
  }

  private drawStack(state: GameState): void {
    const anim = state.clearAnim;
    const clearing = anim ? new Set(anim.rows) : null;
    const flashing = anim !== null && anim.elapsedMs < TUNING.lineClearFlashMs;
    const collapse = anim ? collapseProgress(anim) : 0;

    const top = TUNING.board.bufferHeight;
    const cells = state.board.snapshot();

    for (let y = 0; y < TUNING.board.height; y++) {
      const isClearing = clearing?.has(y) ?? false;
      if (isClearing && !flashing) continue;

      // Rows above a cleared row slide down as it collapses.
      let shift = 0;
      if (anim && !isClearing) {
        const below = anim.rows.reduce((n, r) => (r > y ? n + 1 : n), 0);
        shift = below * LAYOUT.cell * collapse;
      }

      for (let x = 0; x < TUNING.board.width; x++) {
        const cell: Cell = cells[y * TUNING.board.width + x];
        if (!cell) continue;
        const px = FIELD_X + x * LAYOUT.cell;
        const py = FIELD_Y + (y - top) * LAYOUT.cell + shift;
        if (py + LAYOUT.cell < FIELD_Y || py > FIELD_Y + FIELD_H) continue;
        this.painter.blockCell(px, py, LAYOUT.cell, isClearing ? '#ffffff' : PIECE_COLORS[cell]);
      }
    }
  }

  /**
   * Accent band over the rows a clear vacated, drawn above the sliding blocks
   * and fading out with the collapse.
   */
  private drawClearBand(state: GameState): void {
    const anim = state.clearAnim;
    if (!anim) return;
    const collapse = collapseProgress(anim);
    if (collapse <= 0) return;

    const p = this.painter;
    const c = palette();
    const top = TUNING.board.bufferHeight;
    const ctx = this.surface.ctx;

    ctx.save();
    ctx.globalAlpha = 1 - collapse;
    for (const [first, last] of contiguousRuns(anim.rows)) {
      const y = FIELD_Y + (first - top) * LAYOUT.cell;
      const h = (last - first + 1) * LAYOUT.cell;
      p.fillRect(FIELD_X, y, FIELD_W, h, c.clearBand);
      p.fillRect(FIELD_X, y, FIELD_W, 3, c.accent);
      p.fillRect(FIELD_X, y + h - 3, FIELD_W, 3, c.accent);
    }
    ctx.restore();
  }

  /**
   * Re-arm the trail on every row a soft-dropping piece descends, so it drags
   * a short tail for as long as the key is held. Nothing is emitted for
   * ordinary gravity — the tail is feedback for the input, not for the fall.
   */
  private trackSoftDrop(state: GameState): void {
    const piece = state.active;
    if (!piece || state.phase !== Phase.Playing || !state.isSoftDropping()) {
      this.lastActive = null;
      this.softSpan = 0;
      return;
    }

    const previous = this.lastActive;
    this.lastActive = { type: piece.type, y: piece.y };
    if (!previous || previous.type !== piece.type) return;

    const moved = piece.y - previous.y;
    if (moved <= 0) return;

    this.softSpan = Math.min(SOFT_TRAIL_ROWS, this.softSpan + moved);
    this.trail = {
      cells: state.activeCells(),
      distance: this.softSpan,
      color: PIECE_COLORS[piece.type],
      ms: SOFT_TRAIL_MS,
      lifeMs: SOFT_TRAIL_MS,
      intensity: SOFT_TRAIL_INTENSITY,
    };
  }

  /** The swept columns of the last drop, denser toward the landing row. */
  private drawTrail(): void {
    const trail = this.trail;
    if (!trail) return;
    const ctx = this.surface.ctx;
    const p = this.painter;
    const top = TUNING.board.bufferHeight;
    const decay = (trail.ms / trail.lifeMs) * trail.intensity;
    const f = faces(trail.color);

    ctx.save();
    for (const cell of trail.cells) {
      const landing = cell.y - top;
      for (let y = landing - trail.distance; y < landing; y++) {
        if (y < 0) continue;
        ctx.globalAlpha = (0.05 + 0.4 * (y / LAYOUT.rows)) * decay;
        p.bevelRect(
          FIELD_X + cell.x * LAYOUT.cell,
          FIELD_Y + y * LAYOUT.cell,
          LAYOUT.cell,
          LAYOUT.cell,
          f,
        );
      }
    }
    ctx.restore();
  }

  private drawGhost(state: GameState): void {
    const cells = state.ghostCells();
    if (cells.length === 0) return;
    const top = TUNING.board.bufferHeight;
    const ghost = palette().ghost;
    for (const c of cells) {
      this.painter.blockCell(
        FIELD_X + c.x * LAYOUT.cell,
        FIELD_Y + (c.y - top) * LAYOUT.cell,
        LAYOUT.cell,
        ghost,
      );
    }
  }

  private drawActive(state: GameState): void {
    const piece = state.active;
    if (!piece) return;
    const top = TUNING.board.bufferHeight;
    const color = PIECE_COLORS[piece.type];
    for (const c of state.activeCells()) {
      this.painter.blockCell(
        FIELD_X + c.x * LAYOUT.cell,
        FIELD_Y + (c.y - top) * LAYOUT.cell,
        LAYOUT.cell,
        color,
      );
    }
  }

  private drawLevelPlaque(alpha: number): void {
    const p = this.painter;
    const c = palette();
    const ctx = this.surface.ctx;
    const label = 'LEVEL UP';
    const value = formatCounter(this.flashedLevel);
    const w = Math.max(p.measureTyped(label, TYPE.label), p.measureTyped(value, TYPE.levelNumeral)) + 66;
    const h = 14 * 2 + 7 * 2 + TYPE.label.size + 8 + TYPE.levelNumeral.size;
    const x = FIELD_X + (FIELD_W - w) / 2;
    const y = FIELD_Y + (FIELD_H - h) / 2;

    ctx.save();
    ctx.globalAlpha = alpha;
    p.bevelRect(x, y, w, h, c.accentFaces, 7);
    p.text(label, x + w / 2, y + 7 + 14 + TYPE.label.size, {
      size: TYPE.label.size,
      weight: TYPE.label.weight,
      tracking: 0.3,
      color: c.onAccentDim,
      align: 'center',
    });
    p.typed(
      value,
      x + w / 2,
      y + h - 7 - 14 - TYPE.levelNumeral.size * 0.12,
      TYPE.levelNumeral,
      c.onAccent,
      'center',
    );
    ctx.restore();
  }

  private drawLeftColumn(state: GameState, view: GameView): void {
    const p = this.painter;
    const c = palette();
    const x = LAYOUT.pad;

    p.label('PLAYER', x, FIELD_Y + 8);
    const playerType = { size: 15, weight: 700, tracking: 0.14 };
    p.typed(p.fit(view.playerName, LAYOUT.leftWidth, playerType), x, FIELD_Y + 28, playerType, c.text);
    // Which rule set is running, stated where the player is named.
    p.chip(view.mode, x, 106, MODE_COLORS[view.mode]);

    p.label('HOLD', x, 146);
    const boxY = 152;
    const boxH = 64;
    p.bevelRect(x, boxY, LAYOUT.leftWidth, boxH, c.panel, 4);
    if (state.hold) {
      this.drawMiniPiece(state.hold, x, boxY, LAYOUT.leftWidth, boxH, state.canHold ? 1 : 0.3);
    }

    p.label('NEXT', x, NEXT_LABEL_Y);
    p.bevelRect(x, NEXT_BOX_Y, LAYOUT.leftWidth, NEXT_BOX_H, c.panel, 4);
    state.preview(TUNING.previewCount).forEach((type, i) => {
      const rowY = NEXT_BOX_Y + 7 + i * NEXT_ROW_HEIGHT;
      this.drawMiniPiece(type, x, rowY, LAYOUT.leftWidth, NEXT_ROW_HEIGHT, i === 0 ? 1 : 0.55);
    });
  }

  /**
   * Every readout sits in a fixed slot. The column used to advance a running
   * `y`, so anything inserted above the award plaque pushed it toward the
   * canvas edge; now nothing above it can move it.
   */
  private drawRightColumn(state: GameState, view: GameView): void {
    const p = this.painter;
    const c = palette();
    const x = RIGHT_X;
    const w = LAYOUT.rightWidth;

    // --- key reference ----------------------------------------------------
    p.label('KEYS', x, KEYS_LABEL_Y);
    let keyY = KEYS_FIRST_Y;
    for (const [name, key] of GAME_KEYS) {
      p.typed(name, x, keyY, TYPE.keyName, c.textFaint);
      p.typed(key, x + w, keyY, TYPE.keyValue, c.textDim, 'right');
      keyY += KEYS_PITCH;
    }

    // --- score panel ------------------------------------------------------
    const score = HUD_SLOTS.score;
    p.bevelRect(x, score.y, w, score.h, c.panel, 4);
    p.label('SCORE', x + 14, score.y + 22);
    p.typed(formatCounter(state.score), x + w - 14, score.y + 52, TYPE.scoreValue, c.accent, 'right');

    // --- lines / level / best, three rows in one panel ---------------------
    const stats = HUD_SLOTS.stats;
    p.bevelRect(x, stats.y, w, stats.h, c.panel, 4);
    const rows: readonly (readonly [string, string, string, number, number])[] = [
      ['LINES', formatCounter(state.lines), c.text, STATS_ROWS.valueSize, 700],
      ['LEVEL', formatCounter(state.level), c.text, STATS_ROWS.valueSize, 700],
      [
        'BEST',
        formatScore(Math.max(view.best, state.score)),
        c.textDim,
        STATS_ROWS.secondarySize,
        500,
      ],
    ];
    rows.forEach(([label, value, color, size, weight], i) => {
      const baseline = STATS_ROWS.firstY + 14 + i * STATS_ROWS.pitch;
      p.typed(
        label,
        STATS_ROWS.x,
        baseline,
        { size: STATS_ROWS.labelSize, weight: 700, tracking: 0.2 },
        c.textLabel,
      );
      p.typed(
        value,
        STATS_ROWS.x + STATS_ROWS.width,
        baseline,
        { size, weight, tracking: 0.04 },
        color,
        'right',
      );
    });

    // --- run counters -----------------------------------------------------
    const run = HUD_SLOTS.run;
    const counters = state.stats;
    p.label('RUN', x, run.y + 8);
    const counterType = { size: 9, weight: 500, tracking: 0.12 };
    p.typed(
      `${counters.piecesPlaced} PCS · ${formatCounter(counters.tetrises)} TET`,
      x,
      run.y + 23,
      counterType,
      c.textDim,
    );
    p.typed(
      `${formatCounter(counters.tspins)} TSPIN · ${formatCounter(Math.max(0, counters.maxCombo))} MAXC`,
      x,
      run.y + 37,
      counterType,
      c.textDim,
    );

    // --- status chips -----------------------------------------------------
    let chipX = x;
    if (state.backToBack) chipX += p.chip('B2B', chipX, HUD_SLOTS.chips.y, '#e72f6f') + 6;
    if (state.combo > 0) p.chip(`COMBO ${state.combo}`, chipX, HUD_SLOTS.chips.y, '#63c9d8');

    // --- award plaque -----------------------------------------------------
    this.drawAward(x, HUD_SLOTS.award.y, w);
  }

  private drawAward(x: number, y: number, w: number): void {
    const award = this.award;
    if (!award) return;
    const p = this.painter;
    const ctx = this.surface.ctx;
    const on = onBlock(award.color);
    const alpha = Math.min(1, award.ms / AWARD_FADE_MS);

    ctx.save();
    ctx.globalAlpha = alpha;
    p.bevelRect(x, y, w, 74, faces(award.color), 5);
    p.typed(award.title, x + 17, y + 27, TYPE.label, on.dim);
    p.text(award.value, x + 17, y + 51, {
      size: 22,
      weight: 800,
      tracking: 0.02,
      color: on.text,
    });
    p.typed(award.sub, x + 17, y + 65, TYPE.chip, on.dim);
    ctx.restore();
  }

  private drawOverlay(state: GameState, view: GameView): void {
    if (state.phase === Phase.Playing) return;
    scrim(this.painter, FIELD_BOX);

    if (state.phase === Phase.Ready) {
      drawReadyOverlay(this.painter, this.hits, FIELD_BOX);
      return;
    }
    if (state.phase === Phase.Paused) {
      drawPauseOverlay(this.painter, this.hits, FIELD_BOX, state);
      return;
    }
    drawGameOverOverlay(this.painter, this.hits, FIELD_BOX, state, view);
  }


  // ------------------------------------------------------------------- pieces

  private drawMiniPiece(
    type: PieceType,
    boxX: number,
    boxY: number,
    boxW: number,
    boxH: number,
    alpha: number,
  ): void {
    drawMiniPiece(this.painter, type, boxX, boxY, boxW, boxH, LAYOUT.miniCell, alpha);
  }

  private withAlpha(alpha: number, draw: () => void): void {
    if (alpha >= 1) {
      draw();
      return;
    }
    const ctx = this.surface.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    draw();
    ctx.restore();
  }
}

/** Header pill: what the app is doing, and the hue that says so. */
export function statusOf(state: GameState): { text: string; color: string } {
  if (state.clearAnim) return { text: 'CLEARING', color: '#efdc38' };
  switch (state.phase) {
    case Phase.Playing:
      return { text: 'PLAYING', color: '#92c745' };
    case Phase.Paused:
      return { text: 'PAUSED', color: '#63c9d8' };
    case Phase.GameOver:
      return { text: 'GAME OVER', color: '#e72f6f' };
    default:
      return { text: 'READY', color: '#63c9d8' };
  }
}

/** Group sorted row indices into `[first, last]` spans of adjacent rows. */
function contiguousRuns(rows: readonly number[]): [number, number][] {
  const sorted = [...rows].sort((a, b) => a - b);
  const runs: [number, number][] = [];
  for (const row of sorted) {
    const last = runs[runs.length - 1];
    if (last && row === last[1] + 1) last[1] = row;
    else runs.push([row, row]);
  }
  return runs;
}

/** Ease-out collapse after the leading white flash. */
export function collapseProgress(anim: ClearAnimation): number {
  const flash = TUNING.lineClearFlashMs;
  if (anim.elapsedMs <= flash) return 0;
  const t = Math.min(1, (anim.elapsedMs - flash) / Math.max(1, anim.durationMs - flash));
  return 1 - (1 - t) ** 3;
}
